import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@packages/fenrir/components/button';
import { Stack } from '@packages/fenrir/components/stack';
import { useNavigateBack } from '@packages/hooks/use-navigate-back/use-navigate-back';
import { useFeatureFlag } from '@packages/platform/feature-flags';
import { TELEMETRY_SCANNERS_ROUTE } from 'ae-constants/routes.constants';

import { ScannerConfigConfirmationDialog } from './scanner-config-confirmation-dialog';

type ScannerConfigFormControlsProps = {
  isDirty: boolean;
  isDisabled: boolean;
  isPending: boolean;
  reset?: () => void;
  handleClick?: () => void;
};

export const ScannerConfigFormControls: React.FC<
  ScannerConfigFormControlsProps
> = ({ isDirty, isDisabled, isPending, handleClick, reset }) => {
  const { t } = useTranslation('');
  const goBack = useNavigateBack(TELEMETRY_SCANNERS_ROUTE);
  const [open, setOpen] = useState<boolean>(false);
  const [scannerConfigFF] = useFeatureFlag('SCANNER_CONFIGURE_ACTIVATE');

  const handleDialogClose = () => setOpen(false);

  const handleDialogDiscard = () => {
    if (reset) {
      reset();
      return;
    }
    goBack();
  };

  return (
    <Stack direction="row" justifyContent="right" width="100%" gap={2}>
      <ScannerConfigConfirmationDialog
        handleDiscard={handleDialogDiscard}
        isOpen={open}
        closeDialog={handleDialogClose}
      />

      <Button
        variant="text"
        onClick={() => {
          if (isDirty) setOpen(true);
          else goBack();
        }}
        disabled={isDisabled}
      >
        {scannerConfigFF
          ? t('telemetryScannerConfig:scannerConfiguration.cancel')
          : t('ui:unsavedChangesWarning.discardChanges')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        disabled={isDisabled}
        loading={isPending}
        {...(handleClick
          ? { type: 'button', onClick: handleClick }
          : { type: 'submit' })}
      >
        {scannerConfigFF
          ? t('telemetryScannerConfig:scannerConfiguration.saveChanges')
          : t('telemetryScannerConfig:scannerConfiguration.updateButtonText')}
      </Button>
    </Stack>
  );
};
